import { BaseState } from './BaseState';
import { Subject, BehaviorSubject } from 'rxjs';
import { Point } from '../../models/Point';

/**
 * Manages view-related state (zoom, pan, viewport)
 */
export class ViewState extends BaseState {
  // RxJS subjects for reactive state management
  public zoom$ = new BehaviorSubject<number | null>(null);
  public pan$ = new BehaviorSubject<boolean>(false);

  private state = {
    // Zoom state
    zl: null as number | null,
    zlc: 1,
    
    // Pan state
    pan: false,
    isPanning: false,
    pan_tx: null as number | null,
    pan_ty: null as number | null,
    pan_start_x: null as number | null,
    pan_start_y: null as number | null,
    pan_mat: null as any,
    
    // Angle snap
    mag1: true,
    mag2: true,
    angle_snap: false,
  };

  constructor() {
    super();
  }

  getState() {
    return { ...this.state };
  }

  updateState(updates: Partial<typeof this.state>) {
    this.state = { ...this.state, ...updates };
    this.emitStateChange(this.state);
  }

  // Zoom management
  getZoomLevel(): number | null {
    return this.state.zl;
  }

  setZoomLevel(level: number | null) {
    this.state.zl = level;
    this.zoom$.next(level);
  }

  getZoomCoefficient(): number {
    return this.state.zlc;
  }

  setZoomCoefficient(coefficient: number) {
    this.state.zlc = coefficient;
    this.emitStateChange({ zlc: coefficient });
  }

  // Pan management
  isPanning(): boolean {
    return this.state.isPanning;
  }

  setPanning(panning: boolean) {
    this.state.isPanning = panning;
    this.emitStateChange({ isPanning: panning });
  }

  isPanEnabled(): boolean {
    return this.state.pan;
  }

  setPanEnabled(enabled: boolean) {
    this.state.pan = enabled;
    this.pan$.next(enabled);
  }

  getPanTranslation(): { tx: number | null, ty: number | null } {
    return {
      tx: this.state.pan_tx,
      ty: this.state.pan_ty
    };
  }

  setPanTranslation(tx: number | null, ty: number | null) {
    this.state.pan_tx = tx;
    this.state.pan_ty = ty;
    this.emitStateChange({ pan_tx: tx, pan_ty: ty });
  }

  getPanStart(): { x: number | null, y: number | null } {
    return {
      x: this.state.pan_start_x,
      y: this.state.pan_start_y
    };
  }

  setPanStart(x: number | null, y: number | null) {
    this.state.pan_start_x = x;
    this.state.pan_start_y = y;
    this.emitStateChange({ pan_start_x: x, pan_start_y: y });
  }

  getPanMatrix(): any {
    return this.state.pan_mat;
  }

  setPanMatrix(matrix: any) {
    this.state.pan_mat = matrix;
    this.emitStateChange({ pan_mat: matrix });
  }

  // Angle snap management
  isMag1Enabled(): boolean {
    return this.state.mag1;
  }

  setMag1Enabled(enabled: boolean) {
    this.state.mag1 = enabled;
    this.emitStateChange({ mag1: enabled });
  }

  isMag2Enabled(): boolean {
    return this.state.mag2;
  }

  setMag2Enabled(enabled: boolean) {
    this.state.mag2 = enabled;
    this.emitStateChange({ mag2: enabled });
  }

  isAngleSnapEnabled(): boolean {
    return this.state.angle_snap;
  }

  setAngleSnapEnabled(enabled: boolean) {
    this.state.angle_snap = enabled;
    this.emitStateChange({ angle_snap: enabled });
  }
}
